import React from 'react';
import {
  Paper,
  Typography,
  Box,
  Chip,
  Divider
} from '@mui/material';
import Grid from '@mui/material/GridLegacy';
import {
  Home as HomeIcon,
  Straighten as StraightenIcon,
  AccountBalance as AccountBalanceIcon,
  CalendarToday as CalendarIcon,
  LocationCity as LocationCityIcon,
  Person as PersonIcon
} from '@mui/icons-material';
import { PropertyData } from '../types/api.types';

interface PropertyOverviewProps {
  data: PropertyData;
}

const PropertyOverview: React.FC<PropertyOverviewProps> = ({ data }) => {
  // Early return if no data available
  if (!data) {
    return (
      <Paper sx={{ p: 4, borderRadius: 3 }}>
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            Loading Property Data...
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Please wait while we fetch the property information.
          </Typography>
        </Box>
      </Paper>
    );
  }

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0,
    }).format(value);
  };

  const formatArea = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      maximumFractionDigits: 0,
    }).format(value);
  };

  return (
    <Paper sx={{ p: 4, borderRadius: 3 }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" gutterBottom sx={{ fontWeight: 600 }}>
          {data.address || 'Property Address'}
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <Chip
            icon={<LocationCityIcon />}
            label={data.zoning_district || 'Unknown Zone'}
            color="primary"
            sx={{ fontWeight: 500 }}
          />
          <Typography variant="body2" color="text.secondary">
            Parcel ID: {data.parcel_id || 'N/A'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Owner: {data.owner_name || 'N/A'}
          </Typography>
        </Box>
      </Box>

      <Divider sx={{ my: 3 }} />

      <Grid container spacing={4}>
        {/* Property Details */}
        <Grid item xs={12} md={6}>
          <Box>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <HomeIcon color="primary" />
              Property Details
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">Building Type</Typography>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  {data.building?.building_type?.replace(/_/g, ' ') || 'Unknown'}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">Year Built</Typography>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  {data.building?.year_built || 'N/A'}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">Units</Typography>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  {data.building?.units || 0}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">Stories</Typography>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  {data.building?.stories || 'N/A'}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">Bedrooms</Typography>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  {data.building?.bedrooms || 0}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">Bathrooms</Typography>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  {data.building?.bathrooms || 0}
                </Typography>
              </Grid>
            </Grid>
          </Box>
        </Grid>

        {/* Dimensions */}
        <Grid item xs={12} md={6}>
          <Box>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <StraightenIcon color="primary" />
              Dimensions
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">Lot Area</Typography>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  {formatArea(data.geometry?.lot_area || 0)} sq ft
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">Building Area</Typography>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  {formatArea(data.building?.building_area || 0)} sq ft
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">Lot Width</Typography>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  {(data.geometry?.lot_width || 0).toFixed(1)} ft
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">Lot Depth</Typography>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  {(data.geometry?.lot_depth || 0).toFixed(1)} ft
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">Frontage</Typography>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  {(data.geometry?.frontage || 0).toFixed(1)} ft
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">Corner Lot</Typography>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  {data.geometry?.is_corner_lot ? 'Yes' : 'No'}
                </Typography>
              </Grid>
            </Grid>
          </Box>
        </Grid>

        {/* Assessment */}
        <Grid item xs={12}>
          <Box>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <AccountBalanceIcon color="primary" />
              Assessment (FY{data.assessment?.fiscal_year || new Date().getFullYear()})
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={3}>
                <Paper sx={{ p: 2, bgcolor: 'primary.main', color: 'white', textAlign: 'center' }}>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>Total Value</Typography>
                  <Typography variant="h5" sx={{ fontWeight: 600 }}>
                    {formatCurrency(data.assessment?.total_value || 0)}
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={3}>
                <Paper sx={{ p: 2, bgcolor: 'grey.100', textAlign: 'center' }}>
                  <Typography variant="body2" color="text.secondary">Land Value</Typography>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {formatCurrency(data.assessment?.land_value || 0)}
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={3}>
                <Paper sx={{ p: 2, bgcolor: 'grey.100', textAlign: 'center' }}>
                  <Typography variant="body2" color="text.secondary">Building Value</Typography>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {formatCurrency(data.assessment?.building_value || 0)}
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={3}>
                <Paper sx={{ p: 2, bgcolor: 'grey.100', textAlign: 'center' }}>
                  <Typography variant="body2" color="text.secondary">Annual Taxes</Typography>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {formatCurrency(data.assessment?.annual_taxes || 0)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Rate: {((data.assessment?.tax_rate || 0) * 100).toFixed(3)}%
                  </Typography>
                </Paper>
              </Grid>
            </Grid>
          </Box>
        </Grid>
      </Grid>
    </Paper>
  );
};

export default PropertyOverview;